
import React, { useState, useEffect } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { differenceInDays } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { Tent } from "lucide-react";

interface CampingInfoProps {
  permitType: string;
  campingDays: number;
  setCampingDays: (days: number) => void;
  fishingStart?: Date;
  fishingEnd?: Date;
  campingStartDate?: Date;
  setCampingStartDate: (date: Date | undefined) => void;
  campingEndDate?: Date;
  setCampingEndDate: (date: Date | undefined) => void;
}

export const CampingInfo: React.FC<CampingInfoProps> = ({
  permitType,
  campingDays,
  setCampingDays,
  fishingStart,
  fishingEnd,
  campingStartDate,
  setCampingStartDate,
  campingEndDate,
  setCampingEndDate
}) => {
  const { toast } = useToast();
  const isAnnualPermit = permitType.startsWith("annual");
  const [includeCamping, setIncludeCamping] = useState(true);
  const [skipReason, setSkipReason] = useState<string>("");
  const [userModifiedDates, setUserModifiedDates] = useState(false);

  // Use parent camping dates as local aliases
  const campStart = campingStartDate;
  const campEnd = campingEndDate;

  // Calculate nights between two dates (exclusive, hotel-style)
  const calculateNightsBetween = (start: Date, end: Date) => {
    const s = new Date(start);
    s.setHours(0, 0, 0, 0);
    const e = new Date(end);
    e.setHours(0, 0, 0, 0);
    const diffDays = Math.round(differenceInDays(e, s));
    return Math.max(diffDays, 1);
  };

  // Calculate fishing nights (exclusive: 3 fishing days = 2 camping nights)
  const calculateFishingNights = () => {
    if (!fishingStart || !fishingEnd) return 1;
    return calculateNightsBetween(fishingStart, fishingEnd);
  };

  // Initialize camping dates to match fishing dates when component mounts
  useEffect(() => {
    if (isAnnualPermit || !fishingStart || !fishingEnd) return;

    // Only set initial dates if not already set (e.g. from edit mode)
    if (!campingStartDate) setCampingStartDate(fishingStart);
    if (!campingEndDate) setCampingEndDate(fishingEnd);

    if (includeCamping && !campingStartDate && !campingEndDate) {
      const fishingNights = calculateFishingNights();
      setCampingDays(fishingNights);
    }
  }, []); // Only run once on mount

  // Auto-sync camping dates with fishing dates when fishing dates change (unless user modified)
  useEffect(() => {
    if (!fishingStart || !fishingEnd || isAnnualPermit || userModifiedDates) return;

    setCampingStartDate(fishingStart);
    setCampingEndDate(fishingEnd);

    if (includeCamping) {
      const fishingNights = calculateFishingNights();
      setCampingDays(fishingNights);
    }
  }, [fishingStart, fishingEnd, isAnnualPermit, includeCamping, userModifiedDates]);

  // Update camping days when camping dates change
  useEffect(() => {
    if (!includeCamping) {
      setCampingDays(0);
      return;
    }
    if (campStart && campEnd) {
      const nights = calculateNightsBetween(campStart, campEnd);
      setCampingDays(nights);
    } else {
      setCampingDays(0);
    }
  }, [campStart, campEnd, includeCamping]);

  // Handle date changes from the date picker
  const handleDateChange = (startDate: Date | undefined, endDate: Date | undefined) => {
    setCampingStartDate(startDate);
    setCampingEndDate(endDate);
    setUserModifiedDates(true);

    if (!startDate && !endDate) {
      setIncludeCamping(false);
      setCampingDays(0);
      toast({
        title: "Camping removed",
        description: "Please select a reason below for not including camping.",
      });
      setTimeout(() => {
        document.getElementById('skip-reason-trigger')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  // Handle change to camping checkbox
  const handleIncludeCampingChange = (checked: boolean) => {
    if (!checked && skipReason === "") {
      toast({
        title: "Reason Required",
        description: "Please select a reason for not including camping",
        variant: "destructive"
      });
      return;
    }
    setIncludeCamping(checked);

    if (!checked) {
      setCampingDays(0);
    } else {
      setSkipReason("");
      if (!userModifiedDates && fishingStart && fishingEnd) {
        setCampingStartDate(fishingStart);
        setCampingEndDate(fishingEnd);
        const fishingNights = calculateFishingNights();
        setCampingDays(fishingNights);
      } else if (campStart && campEnd) {
        const nights = calculateNightsBetween(campStart, campEnd);
        setCampingDays(nights);
      }
    }
  };

  if (isAnnualPermit) {
    return <div className="mb-8 bg-tribal-primary/5 p-4 rounded-md text-sm">
        <ul className="list-disc pl-5 space-y-2">
          <li>Annual permits include general camping at non-electrical sites.</li>
          <li>Family Permit: Valid for spouses and their children (ages 17 and under). Children 18+ require their own permit. This permit covers fishing for immediate family members only.</li>
        </ul>
      </div>;
  }

  return <div className="mb-8">
      <h3 className="font-semibold text-lg text-tribal-secondary mb-4 flex items-center">
        <Tent className="mr-2 h-5 w-5 text-tribal-primary" />
        Camping Options
      </h3>

      <div className="flex items-center space-x-2 mb-4">
        <Checkbox id="include-camping" checked={includeCamping} onCheckedChange={handleIncludeCampingChange} />
        <Label htmlFor="include-camping" className="font-medium cursor-pointer">
          Include general camping permit
        </Label>
      </div>

      <div className="mb-4">
        <Label htmlFor="skip-reason" className="mb-2 block text-sm">
          Not camping overnight? Select your reason below to remove camping charges {!includeCamping && <span className="text-red-500">*</span>}
        </Label>
        <Select value={skipReason} onValueChange={value => {
        setSkipReason(value);
        if (value !== "") {
          setIncludeCamping(false);
          setCampingDays(0);
        }
      }}>
          <SelectTrigger id="skip-reason-trigger" className="w-full">
            <SelectValue placeholder={includeCamping ? "Select a reason (optional)" : "Select a reason"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day-trip">Day trip only (no overnight stay)</SelectItem>
            <SelectItem value="electrical">Reserved electrical campsite separately</SelectItem>
            <SelectItem value="motel">Staying in local motel</SelectItem>
            <SelectItem value="departing">Departing reservation overnight (returning next day)</SelectItem>
            <SelectItem value="other">Other accommodation arrangement</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {includeCamping && <div className="bg-tribal-primary/5 p-4 rounded-md">
          <p className="text-sm text-tribal-neutral mb-3">Camping begins at 2:00 PM and ends at 11:00 AM the following day.</p>
          <DateRangePicker startDate={campStart} endDate={campEnd} setStartDate={date => handleDateChange(date, campEnd)} setEndDate={date => handleDateChange(campStart, date)} startLabel="First Night Camping" endLabel="Last Night Camping" placeholder="Select your camping dates" disabled={!includeCamping} displayUnit="nights" showRangeCount={false} />
          
          <div className="font-medium text-sm mt-4 mb-2">
            {campingDays > 0 ? <>
                {campingDays} night{campingDays !== 1 ? 's' : ''} of camping ($15.00 per night)
              </> : <>Please select valid camping dates</>}
          </div>
        </div>}
    </div>;
};
